import React, {useEffect, useState, useRef} from 'react';
import {
    Checkbox,
    createTheme,
    TextField,
    Tooltip,
    Button,
    Badge, InputAdornment,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PropTypes from 'prop-types';
import {makeStyles} from '@mui/styles';
import {MasterDataDropdown} from '../utils/MasterDataDropdown.jsx';
import {DeliveryAddressField} from '../general_details/header_details/DeliveryAddressField.jsx';
import {
    requestStores,
    useApplicationStore,
} from '../../ListOfPREditors.brick.jsx';
import produce from 'immer';
import {
    fetchAddressFromPlant,
    fetchAddressFromStorageLocation,
} from '../../../utils/address';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {NumberTextfieldSmall} from '../utils/NumberTextfieldSmall.jsx';
import {LimitedLengthTextfield} from '../utils/LimitedLengthTextfield.jsx';
import {Material} from '../../../models/Material';
import enUS from 'date-fns/locale/en-US';
import {formatMaterialName} from '../../../utils/helpers';
import ThreeDotsMenu from '../menus/ThreeDotsMenu.jsx';
import CustomPopup from '../../dialogs/CustomPopup.jsx';
import {UnitOfMeasureFieldWithPopup} from './UnitOfMeasureFieldWithPopup.jsx';
import {CurrencyController} from '../../../controllers/CurrencyController.js';
import {TaxCodeAutomation} from './TaxCodeAutomation.jsx';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PriorityHighIcon from '@mui/icons-material/PriorityHighRounded';
import {
    TRANSPORT_OPTIONS_ENUM,
    mandatoryFieldsForRequester,
    mandatoryFieldsForSpender,
    SOURCES, LAMBDA_NAMES, SOURCES_TO_DISPLAY,
} from '../../../utils/constants';
import {
    bgColorRequester,
    bgColorSpender, complianceIndicatorOptions,
    ezprOrange,
    getUniquePrId,
} from '../../_helpers';
import {Auth, CloudObject, Color, Config, BrickContext} from 'olympe';
import {isDateValid} from '../../../utils/date.js';
import {formatCurrencySeparator} from '../../../utils/number.js';
import {
    copyLambda, getL3CategoryFromMaterialPromiseLambda,
    getPSLStatusLambda, getTaxCodeFromMaterialLambda,
    getUserDefaultsLambda,
    getUserInfoByUserIDLambda,
    saveLambda,
    setToChangeLogLambda,
    showPopupLambda,
} from '../../../utils/functionSignatures';
import {PurchaseRequest} from '../../../models/PurchaseRequest';
import {createChangeLog, createCopyChangeLog} from '../../../utils/changeLog';
import {tagToDisplayER} from '../../../utils/tagTransforms';
import {FormatCurrencySeparatorTextfield} from '../utils/FormatCurrencySeparatorTextfield.jsx';
import UserController from '../../../controllers/UserController';
import CircularProgress from '@mui/material/CircularProgress';
import TwoToneCheckbox from '../../TwoToneCheckbox.jsx';
import {ItemCodeDescriptionDropdown} from '../../dialogs/dyn/ItemCodeDescriptionDropdown.jsx';
import {checkLabelValueObject} from '../../../utils/checkObjects';
import {DISABLE_REASONS} from '../utils/fieldEditability';
import {useFieldEditability} from '../utils/useFieldEditability.js';

const PRIORITY_HIGH_ICON_SX = {fontSize: '18px'};

// These reasons do not switch the unit control to the popup.
const GLOBAL_DISABLE_REASONS = new Set([
    DISABLE_REASONS.SUBMITTED_TAB,
    DISABLE_REASONS.PR_LOCKED,
    DISABLE_REASONS.NO_ACCESS,
    DISABLE_REASONS.PENDING_SB,
]);

/**
 * @param {LineItem.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function LineItem(props) {
    const [expanded, setExpanded] = useState(false);
    const [removePopupOpen, setRemovePopupOpen] = useState(false);
    const [ungroupPopupOpen, setUngroupPopupOpen] = useState(false);
    const [showAPIErrorPopup, setShowAPIErrorPopup] = useState(false);
    const [errorReason, setErrorReason] = useState('');
    const [showCurrencyErrorPopup, setShowCurrencyErrorPopup] = useState(false);
    const [showErrorTooltip, setShowErrorTooltip] = useState('');
    const [deliveryAddressLoading, setDeliveryAddressLoading] = useState(false);
    const [materialNameLoading, setMaterialNameLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [pendingUpdates, setPendingUpdates] = useState({});
    const useRequestStore = requestStores.get(props.prTag);
    const [materials, setMaterials, updateAllMaterials] = useRequestStore((state) => [
        state.materials,
        state.setMaterials,
        state.updateAllMaterials,
    ]);
    const material = materials.find((m) => m.id === props.materialId);
    const {isDisabled, getReasons} = useFieldEditability(props.prTag, {
        context: props.context,
        material,
    });
    const unitReasons = getReasons('lineItem.unit');
    const showUnitPopup = unitReasons.some((reason) => !GLOBAL_DISABLE_REASONS.has(reason));
    const unitPopupDisabled = unitReasons.some((reason) =>
        reason === DISABLE_REASONS.NO_ACCESS || reason === DISABLE_REASONS.SOURCE_NSC,
    );
    const deleteMaterial = useRequestStore((state) => state.deleteMaterial);
    const deleteMaterials = useRequestStore((state) => state.deleteMaterials);
    const duplicateMaterial = useRequestStore((state) => state.duplicateMaterial);
    const setDeletedItem = useRequestStore((state) => state.setDeletedItem);
    const setCopiedItem = useRequestStore((state) => state.setCopiedItem);
    const userIdToCallSAPAPIs = useRequestStore(
        (state) => state.userIdToCallSAPAPIs,
    );
    const [
        highlightMandatoryFieldForRequester,
        highlightMandatoryFieldForSpender,
    ] = useRequestStore((state) => [
        state.highlightMandatoryFieldForRequester,
        state.highlightMandatoryFieldForSpender,
    ]);
    const getPurchaseRequestFromStore = useRequestStore(
        (state) => state.getPurchaseRequest,
    );
    const source = useRequestStore((state) => state.source);
    const sourceToDisplay = useRequestStore((state) => state.sourceToDisplay);
    const isPunchOut = source === SOURCES.PUNCHOUT || sourceToDisplay === SOURCES_TO_DISPLAY.PUNCHOUT;
    const isNestBuy = source === SOURCES.NESTBUY;

    const materialHasTransportConditions =
    material.transportCosts &&
    material.transportCostType === TRANSPORT_OPTIONS_ENUM['Via Condition type'];

    const companyCode = useRequestStore((state) => state.companyCode);
    const purchaseOrganization = useRequestStore((state) => state.purchaseOrganization);
    const vendorCode = useRequestStore((state) => state.vendorCode);
    const defaultPlant = useRequestStore((state) => state.defaultPlant);

    const conversionFailed = useRequestStore((state) => state.conversionFailed);
    const sourcingEventID = useRequestStore((state) => state.sourcingEventID);
    const contractID = useRequestStore((state) => state.contractID);
    const buyer = useRequestStore((state) => state.buyer);

    const getMasterData = props.context.get(LAMBDA_NAMES.GET_MASTER_DATA);
    const showPopup = props.context.get(LAMBDA_NAMES.SHOW_POPUP);
    const getUserInfoByUserID = props.context.get(LAMBDA_NAMES.GET_USER_INFO_BY_USER_ID);
    const save = props.context.get(LAMBDA_NAMES.SAVE);
    const copy = props.context.get(LAMBDA_NAMES.COPY);
    const setToChangeLog = props.context.get(LAMBDA_NAMES.SET_TO_CHANGE_LOG);
    const getPSLStatus = props.context.get(LAMBDA_NAMES.GET_PSL_STATUS);
    const getMaterial = props.context.get(LAMBDA_NAMES.OG_ITEM_CODE_DESC);
    const getL3Category = props.context.get(LAMBDA_NAMES.GET_L3_CATEGORY);

    const editableDescription = ![
        'ERSA',
        'UNBW',
        'FERT',
        'HALB',
        'ZGNV',
        'ZTAS',
    ].includes(material.type);

    const oldPerValue = useRef(material.per);
    const oldUnitValue = useRef(null);

    const dateFormat = useApplicationStore((state) => state.userDateFormat);
    const separator = useApplicationStore((state) => state.userCurrencySeparator);

    const isSelected = props.selectedMaterialIds.includes(props.materialId);

    const minDate = new Date(Date.now());

    const currentPr = getPurchaseRequestFromStore().getPurchaseRequest();

    const userDefaultsLambda = props.context.get(LAMBDA_NAMES.GET_USER_DEFAULT_INFO);

    const showTaxAmount =
    companyCode.value.substring(0, 2) === 'US' &&
    material.taxCode?.value === 'I1';
    const expandedHeight = showTaxAmount ? '600px' : '550px';

    const [taxCodeFromMaterial, setTaxCodeFromMaterial] = useState('');

    useEffect(() => {
        let cancelled = false;

        async function fetchTaxCode() {
            if (material && material.name && material.plant) {
                try {
                    const dashIndex = material.name.indexOf('-');
                    const materialNumber =
                        dashIndex !== -1 ? material.name.substring(0, dashIndex).trim() : material.name;
                    const taxCodeResponse = await getTaxCodeFromMaterialLambda(props.context, props.context.get(LAMBDA_NAMES.GET_TAX_CODE_FROM_MATERIAL), materialNumber, material.plant.value);
                    const parsedTaxCodeResponse = JSON.parse(taxCodeResponse);
                    let taxCodeValue = '';
                    if (Array.isArray(parsedTaxCodeResponse) && parsedTaxCodeResponse.length > 0) {
                        taxCodeValue = parsedTaxCodeResponse[0].glb_txcode;
                    }
                    if (!cancelled) setTaxCodeFromMaterial(taxCodeValue);
                } catch (error) {
                    console.error('Error fetching tax code from material:', error);
                    if (!cancelled) setTaxCodeFromMaterial('');
                }
            }
        }
        fetchTaxCode();

        return () => {
            cancelled = true;
        };
    }, [material.name, material.plant]);

    // This useEffect fetch the base unit if it is empty
    // TODO Refactor the exchange rate fetch in TotalBudget.jsx to make it work
    // useEffect(() => {
    //     const materialWithoutBaseUnit = new Set(materials.filter((m) => !m.baseUnit).map((m) => m.name.split(' - ')[0]?.padStart(18, '0')));
    //     if (materialWithoutBaseUnit.size > 0) {
    //         getBaseUnitsLambda(props.context, props.context.get('Get base units'), Array.from(materialWithoutBaseUnit))
    //             .then((result) => {
    //                 const parsedResult = JSON.parse(result[0]);
    //
    //                 const newMaterials = produce(materials, (draft) => {
    //                     draft.forEach((mat) => {
    //                         if (!mat.baseUnit) {
    //                             mat.baseUnit = parsedResult.find((m) => m.matnr === mat.name.split(' - ')[0]?.padStart(18, '0')).meins;
    //                         }
    //                     });
    //                 });
    //
    //                 updateAllMaterials(newMaterials);
    //             });
    //     }
    // }, []);

    // This useEffect sets the oldUnitValue ref to the per value
    useEffect(() => {
        oldUnitValue.current = material.per;
    }, []);

    const handleCheckboxChange = () => {
        props.onSelectMaterial(props.materialId);
    };

    const handleItemCodeChange = async (value) => {
        const updates = {};

        setMaterialNameLoading(true);
        const l3Category = await getL3CategoryFromMaterialPromiseLambda(props.context, getL3Category, value.value.padStart(18, '0'));
        setMaterialNameLoading(false);
        const parsedL3 = JSON.parse(l3Category[0])[0];

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.name = value.label;
            if (value.value) {
                currentMat.l3Category = `${parsedL3.matkl} - ${parsedL3.wgbez}`;
                updates.l3Category = currentMat.l3Category;
            }
            updates.name = currentMat.name;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleDescriptionChange = (value) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.description = value;
            updates.description = currentMat.description;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleQuantityChange = (value) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.quantity = parseFloat(value) || 0.0;
            updates.quantity = currentMat.quantity;
            if (currentMat.freeItem) {
                currentMat.per = currentMat.quantity * material.price;
                updates.per = currentMat.per;
            }
            // If don't select the unit initially, the unit value of the dropdown equals baseUnit but on saving PR, material.unit is empty
            if (!currentMat.unit) {
                currentMat.unit = material.baseUnit;
                updates.unit = currentMat.unit;
            }
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleUnitPriceChange = (newVal) => {
        const updates = {};

        // Check if the values are coming from X button or free typing.
        let newMaterials;
        if (!newVal.value) {
            newMaterials = produce(materials, (draft) => {
                const currentMat = draft.find((m) => m.id === material.id);
                currentMat.unit = '';
                updates.unit = currentMat.unit;
            });
        } else {
            newMaterials = produce(materials, (draft) => {
                const currentMat = draft.find((m) => m.id === material.id);
                currentMat.unit = newVal?.label;
                currentMat.price =
          material.price *
          ((1 / (parseInt(oldUnitValue.current) || 1)) * parseInt(newVal.value));
                oldUnitValue.current = parseInt(newVal.value);
                updates.unit = currentMat.unit;
                updates.price = currentMat.price;
            });
        }
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handlePriceChange = (value) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.price = parseFloat(value) || 0.0;
            updates.price = currentMat.price;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleCurrencyChange = (newVal) => {
        const newMaterials = produce(materials, (draft) => {
            draft.forEach((mat) => {
                mat.currency = newVal;

                // If a currency other than USDN is selected and price has more than 3 decimals, format price to 3 decimals.
                if (
                    newVal !== 'USDN' &&
                    String(mat.price).includes('.') &&
                    String(mat.price).split('.')[1]?.length > 3
                ) {
                    mat.price = mat.price.toFixed(3);
                }
            });
        });

        updateAllMaterials(newMaterials);
    };

    const handlePerChange = (value) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            const newValue = value || currentMat.per;
            currentMat.per = parseInt(newValue, 10);
            oldPerValue.current = parseInt(newValue, 10);
            updates.per = currentMat.per;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleTaxCodeChange = (newVal) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.taxCode = newVal;
            updates.taxCode = currentMat.taxCode;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleRequestedDeliveryDateChange = (newValue) => {
        const updates = {};

        // check if the value is real date, if not don't do anything
        if (isDateValid(newValue)) {
            const newMaterials = produce(materials, (draft) => {
                const currentMat = draft.find((m) => m.id === material.id);
                currentMat.requestedDeliveryDate = newValue;
                updates.requestedDeliveryDate = currentMat.requestedDeliveryDate;
            });
            setMaterials(newMaterials, material.id);
        }

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handlePlantChange = (newVal) => {
        const updates = {};

        setDeliveryAddressLoading(true);
        const plantAddressPromise = newVal.addressNumber ?
            fetchAddressFromPlant(newVal.addressNumber, props.context) :
            Promise.resolve(undefined);
        plantAddressPromise
            .then((plantAddress) => {
                newVal.deliveryAddress = plantAddress;
                const newMaterials = produce(materials, (draft) => {
                    const currentMat = draft.find((m) => m.id === material.id);
                    currentMat.plant = newVal;
                    currentMat.deliveryAddress = plantAddress;
                    updates.plant = currentMat.plant;
                    updates.deliveryAddress = currentMat.deliveryAddress;
                });
                setMaterials(newMaterials, material.id);
            })
            .finally(() => setDeliveryAddressLoading(false));

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleChangeStorageLocation = (newVal) => {
        const updates = {};

        setDeliveryAddressLoading(true);
        const storageLocationAddressPromise = newVal ?
            fetchAddressFromStorageLocation(
                newVal,
                props.context,
                material.plant,
                userIdToCallSAPAPIs,
            ) :
            Promise.resolve(undefined);
        storageLocationAddressPromise
            .then((storageLocationAddress) => {
                newVal.deliveryAddress = storageLocationAddress;
                updates.deliveryAddress = storageLocationAddress;
                const newMaterials = produce(materials, (draft) => {
                    const currentMat = draft.find((m) => m.id === material.id);
                    currentMat.storageLocation = newVal;
                    currentMat.deliveryAddress = {};
                    updates.storageLocation = currentMat.storageLocation;
                });
                setMaterials(newMaterials, material.id);
            })
            .finally(() => setDeliveryAddressLoading(false));

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleDeliveryAddressChange = (newAddress) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.deliveryAddress = newAddress;
            updates.deliveryAddress = currentMat.deliveryAddress;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleFreeItemChange = (event) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.freeItem = event.target.checked;
            currentMat.per = event.target.checked ?
                material.quantity * material.price :
                oldPerValue.current;
            updates.freeItem = currentMat.freeItem;
            updates.per = currentMat.per;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleClickOnDelete = () => {
        setRemovePopupOpen(true);
    };

    const handleClickOnUngroup = () => {
        setUngroupPopupOpen(true);
    };

    const handleClickOnEditTransportCostConditions = () => {
        props.setSelectedTransportCostMaterialId(material.id);
        props.setShowViaConditionPopup(true);
    };

    const handleClickOnDeleteMaterial = () => {
        if (source === SOURCES.CANDEX) {
            setDeletedItem(material.id);
        } else {
            deleteMaterial(material.id);
        }
        setRemovePopupOpen(false);
    };

    const handleShowAPIErrorPopup = (context) => {
        setErrorReason(context);
        setShowAPIErrorPopup(true);
    };

    const handleClickOnUngroupMaterial = async (ungroupAllSelectedItems) => {
        if (ungroupAllSelectedItems && props.selectedMaterialIds.length === materials.length) {
            showPopupLambda(
                props.context,
                showPopup,
                'Ungrouping is not possible as all line items are selected.',
                `PRs must have at least one line item.`,
                Color.create(241, 144, 2),
                'content_copy',
                () => {
                    setUngroupPopupOpen(false);
                },
            );
            return;
        }

        let selectedMaterials = [];
        if (ungroupAllSelectedItems) {
            selectedMaterials = props.selectedMaterialIds.map((materialId) => materials[materialId]);
        } else {
            selectedMaterials = [material];
        }

        const pr = new PurchaseRequest(
            JSON.stringify(getPurchaseRequestFromStore().getPurchaseRequest()),
        );

        const isPSL = (pr.source && (pr.source.toUpperCase() === SOURCES.PSL || pr.source.toUpperCase() === SOURCES.MIXED.toUpperCase())) ||
            (pr.sourceFromApiKey && (pr.sourceFromApiKey.toUpperCase() === SOURCES.PSL || pr.sourceFromApiKey.toUpperCase() === SOURCES.MIXED.toUpperCase()));

        if (isPSL) {
            for (const mat of selectedMaterials) {
                if (!mat.pslId) {
                    showPopupLambda(
                        props.context,
                        showPopup,
                        'Your request has not been ungrouped.',
                        `Since there is no PSL Id for this line item, the material cannot be ungrouped. Please use the PSL to initiate a new PR.`,
                        Color.create(241, 144, 2),
                        'content_copy',
                        () => {
                            setUngroupPopupOpen(false);
                        },
                    );
                    return;
                }
                if (!mat.uniqueSourcingToolId) {
                    showPopupLambda(
                        props.context,
                        showPopup,
                        'Your request has not been ungrouped.',
                        `Since there is no PSL Material Id for this line item, the material cannot be ungrouped. Please use the PSL to initiate a new PR.`,
                        Color.create(241, 144, 2),
                        'content_copy',
                        () => {
                            setUngroupPopupOpen(false);
                        },
                    );
                    return;
                }
            }

            const uniqueSourcingToolIds = selectedMaterials
                .map((material) => material.uniqueSourcingToolId)
                .filter((id) => id);

            if (uniqueSourcingToolIds.length > 0) {
                const statuses = await getPSLStatusLambda(
                    props.context,
                    getPSLStatus,
                    uniqueSourcingToolIds,
                );

                const parsedStatuses = JSON.parse(statuses[0]);

                if (
                    parsedStatuses.data.some((d) => d['pslMaterailStatus'] !== 'Active')
                ) {
                    showPopupLambda(
                        props.context,
                        showPopup,
                        'This request cannot be ungrouped.',
                        `Reason: at least one of its materials is now obsolete (Material PSL ID is inactive).`,
                        Color.create(241, 144, 2),
                        'content_copy',
                        () => {
                            setUngroupPopupOpen(false);
                        },
                    );
                    return;
                }
            }
        }

        const userID = UserController.getCurrentUserName('EZPR-USER');

        let uniquePrId;
        try {
            uniquePrId = await getUniquePrId(props.context);
        } catch (error) {
            console.error(error);
            handleShowAPIErrorPopup(
                'Something went wrong while fetching the new EPRId.',
            );
            return;
        }

        const newMaterials = [];

        if (ungroupAllSelectedItems) {
            props.selectedMaterialIds.forEach((materialId) => {
                newMaterials.push(
                    new Material(
                        newMaterials.length,
                        materials[materialId].name,
                        materials[materialId].description,
                        materials[materialId].quantity,
                        materials[materialId].unit,
                        materials[materialId].baseUnit,
                        materials[materialId].price,
                        materials[materialId].currency,
                        materials[materialId].per,
                        materials[materialId].taxCode,
                        materials[materialId].tax,
                        materials[materialId].l1Category,
                        materials[materialId].l3Category,
                        materials[materialId].requestedDeliveryDate,
                        materials[materialId].plant,
                        materials[materialId].storageLocation,
                        materials[materialId].deliveryAddress,
                        materials[materialId].total,
                        materials[materialId].freeItem,
                        materials[materialId].transportCosts,
                        materials[materialId].supplierPartId,
                        materials[materialId].supplierPartAuxiliaryId,
                        materials[materialId].arePriceDataFromST,
                        materials[materialId].supplierComments,
                        materials[materialId].internalComments,
                        materials[materialId].exchangeCurrencyRate,
                        materials[materialId].type,
                        materials[materialId].supplierStatus,
                        materials[materialId].pslId,
                        materials[materialId].transportCostType,
                        materials[materialId].transportCostConditions,
                        materials[materialId].uniqueSourcingToolId,
                        materials[materialId].ppmCostPlan,
                        materials[materialId].taxAmount,
                        materials[materialId].source,
                    ),
                );
            });
        } else {
            newMaterials.push(
                new Material(
                    0,
                    material.name,
                    material.description,
                    material.quantity,
                    material.unit,
                    material.baseUnit,
                    material.price,
                    material.currency,
                    material.per,
                    material.taxCode,
                    material.tax,
                    material.l1Category,
                    material.l3Category,
                    material.requestedDeliveryDate,
                    material.plant,
                    material.storageLocation,
                    material.deliveryAddress,
                    material.total,
                    material.freeItem,
                    material.transportCosts,
                    material.supplierPartId,
                    material.supplierPartAuxiliaryId,
                    material.arePriceDataFromST,
                    material.supplierComments,
                    material.internalComments,
                    material.exchangeCurrencyRate,
                    material.type,
                    material.supplierStatus,
                    material.pslId,
                    material.transportCostType,
                    material.transportCostConditions,
                    material.uniqueSourcingToolId,
                    material.ppmCostPlan,
                    material.taxAmount,
                    material.source,
                ),
            );
        }

        getUserInfoByUserIDLambda(
            props.context,
            getUserInfoByUserID,
            userID,
            (userInfo) => {
                const parsedUserInfo = JSON.parse(userInfo)[0];
                getUserDefaultsLambda(
                    props.context,
                    userDefaultsLambda,
                    (userDefaults) => {
                        pr.projectTitle = '';
                        pr.prNumber = '';
                        pr.spender =
              userDefaults.spender?.value && userDefaults.spender?.value !== '' ?
                  {...userDefaults.spender} :
                  {
                      value: parsedUserInfo.id,
                      label: parsedUserInfo.description,
                      email: parsedUserInfo.email,
                  };
                        pr.requester =
              userDefaults.requester?.value &&
              userDefaults.requester?.value !== '' ?
                  {...userDefaults.requester} :
                  {
                      value: parsedUserInfo.id,
                      label: parsedUserInfo.description,
                      email: parsedUserInfo.email,
                  };
                        pr.creationDate = new Date().toUTCString();
                        pr.attachmentsInfo.competitiveBiddingJustificationAttachments = [];
                        pr.attachmentsInfo.mainAttachments = [];
                        pr.attachmentsInfo.complianceIndicator = complianceIndicatorOptions[0];
                        pr.attachmentsInfo.exception = false;
                        pr.attachmentsInfo.selectedException = '';
                        pr.status = PurchaseRequest.statuses.created;
                        pr.headerDetails.validityStart = '';
                        pr.headerDetails.validityEnd = '';
                        pr.headerDetails.defaultRequestedDeliveryDate = '';
                        pr.materials.forEach(
                            (material) => (material.requestedDeliveryDate = ''),
                        );
                        pr.submissionDate = '';
                        pr.EPRId = uniquePrId;
                        pr.sbValidation.sbApprover = '';
                        pr.nbsValidation.obApprover = '';
                        if (
                            pr.sendingOptions.otherAddress &&
              pr.sendingOptions.otherAddress !== ''
                        ) {
                            // preparing to remove it also trim the internalComment
                            const otherAddress = pr.sendingOptions.otherAddress
                                .split(';')
                                .join('; ');
                            pr.sendingOptions.otherAddress = '';
                            pr.sendingOptions.p2pOther = false;
                            pr.internalComment = pr.internalComment.replace(otherAddress, '');
                        }
                        if (
                            pr.sendingOptions.supplierAddresses &&
              pr.sendingOptions.supplierAddresses.length > 0
                        ) {
                            // preparing to remove it also trim the internalComment
                            const otherAddress =
                pr.sendingOptions.supplierAddresses.join('; ');
                            pr.sendingOptions.p2pSupplier = false;
                            pr.sendingOptions.vmdSupplier = false;
                            pr.sendingOptions.pslSupplier = false;
                            pr.sendingOptions.supplierAddresses = [];
                            pr.internalComment = pr.internalComment.replace(otherAddress, '');
                        }
                        pr.materials = newMaterials;
                        copyLambda(
                            props.context,
                            copy,
                            JSON.stringify(pr.getPurchaseRequest()),
                            (tag) => {
                                setToChangeLogLambda(
                                    props.context,
                                    setToChangeLog,
                                    createCopyChangeLog(currentPr.EPRId, uniquePrId),
                                    CloudObject.get(Auth.getCurrentUser()),
                                    tag,
                                    () => {
                                        showPopupLambda(
                                            props.context,
                                            showPopup,
                                            'Your item has been ungrouped.',
                                            `This is the ID of your new PR: EPR-${
                                                pr.EPRId || tagToDisplayER(tag)
                                            }`,
                                            Color.create(241, 144, 2),
                                            'content_copy',
                                            () => {
                                                if (ungroupAllSelectedItems) {
                                                    deleteMaterials(props.selectedMaterialIds);
                                                    props.setSelectedMaterialIds([]);
                                                } else {
                                                    deleteMaterial(props.materialId);
                                                    props.setSelectedMaterialIds([]);
                                                }

                                                props.setIsItemUngrouped(true);

                                                saveLambda(
                                                    props.context,
                                                    save,
                                                    JSON.stringify(
                                                        getPurchaseRequestFromStore().getPurchaseRequest(),
                                                    ),
                                                    props.prTag,
                                                    createChangeLog(
                                                        currentPr,
                                                        getPurchaseRequestFromStore().getPurchaseRequest(),
                                                    ),
                                                    CloudObject.get(Auth.getCurrentUser()),
                                                    setToChangeLog,
                                                    () => null,
                                                    () => null,
                                                    // If save fails.
                                                    () => {
                                                        handleShowAPIErrorPopup(
                                                            'Something went wrong while saving the PR.',
                                                        );
                                                    },
                                                    // If change log fails.
                                                    () => {
                                                        handleShowAPIErrorPopup(
                                                            'Something went wrong while creating the change log.',
                                                        );
                                                    },
                                                );
                                            },
                                        );
                                    },
                                    () => {
                                        handleShowAPIErrorPopup(
                                            'Something went wrong while creating the ungrouped PR\'s change log',
                                        );
                                    },
                                );
                            },
                            () => {
                                handleShowAPIErrorPopup(
                                    'Something went wrong while ungrouping the item.',
                                );
                            },
                        );
                    },
                    () => {
                        handleShowAPIErrorPopup(
                            'Something went wrong while fetching user defaults data.',
                        );
                    },
                );
            },
            () => {
                handleShowAPIErrorPopup(
                    'Something went wrong while fetching user data.',
                );
            },
        );

        setUngroupPopupOpen(false);
    };

    const handleClick = () => {
        setExpanded(!expanded);
    };

    const handlePPMCostPlanChange = (value) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.ppmCostPlan = value;
            updates.ppmCostPlan = currentMat.ppmCostPlan;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleSupplierCommentsChange = (value) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.supplierComments = value;
            updates.supplierComments = currentMat.supplierComments;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleInternalCommentsChange = (value) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.internalComments = value;
            updates.internalComments = currentMat.internalComments;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleVendorMaterialNumberChange = (event) => {
        const updates = {};

        const newMaterials = produce(materials, (draft) => {
            const currentMat = draft.find((m) => m.id === material.id);
            currentMat.supplierPartId = event.target.value;
            updates.supplierPartId = currentMat.supplierPartId;
        });
        setMaterials(newMaterials, material.id);

        if (materials.length > 1 && props.selectedMaterialIds.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleClickOnCopy = async () => {
        const pr = new PurchaseRequest(
            JSON.stringify(getPurchaseRequestFromStore().getPurchaseRequest()),
        );

        const isPSL = (pr.source && (pr.source.toUpperCase() === SOURCES.PSL || pr.source.toUpperCase() === SOURCES.MIXED.toUpperCase())) ||
            (pr.sourceFromApiKey && (pr.sourceFromApiKey.toUpperCase() === SOURCES.PSL || pr.sourceFromApiKey.toUpperCase() === SOURCES.MIXED.toUpperCase()));

        if (isPSL) {
            if (!material.pslId) {
                showPopupLambda(
                    props.context,
                    showPopup,
                    'Your line item has not been copied',
                    'Since there is no PSL ID for this line item, it cannot be copied. Please use the PSL to initiate a new PR.',
                    Color.create(241, 144, 2),
                    'content_copy',
                    () => {},
                );
                return;
            }

            if (!material.uniqueSourcingToolId) {
                showPopupLambda(
                    props.context,
                    showPopup,
                    'Your line item has not been copied',
                    'Since there is no PSL Material ID for this line item, it cannot be copied. Please use the PSL to initiate a new PR.',
                    Color.create(241, 144, 2),
                    'content_copy',
                    () => {},
                );
                return;
            }

            const statuses = await getPSLStatusLambda(
                props.context,
                getPSLStatus,
                [material.uniqueSourcingToolId],
            );
            const parsedStatuses = JSON.parse(statuses[0]);
            if (parsedStatuses.data.some((d) => d['pslMaterailStatus'] !== 'Active')) {
                showPopupLambda(
                    props.context,
                    showPopup,
                    'This request cannot be copied.',
                    `Reason: at least one of its materials is now obsolete (Material PSL ID is inactive).`,
                    Color.create(241, 144, 2),
                    'content_copy',
                    () => {},
                );
                return;
            }
        }

        if (source === SOURCES.CANDEX) {
            setCopiedItem(material);
        } else {
            duplicateMaterial(material);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbarOpen(false);
    };

    const handleUpdateAction = () => {
        props.updateSelectedMaterials(pendingUpdates);
        setPendingUpdates({});
        setSnackbarOpen(false);
    };

    const _theme = createTheme();
    const useStyles = makeStyles(
        () => {
            return {
                root: {
                    // input label
                    '& .MuiInput-root': {
                        backgroundColor: '#FFFFFF',
                        borderRadius: '5px',
                    },
                    // focused underline color for input with variant='standard'
                    '& .MuiInput-underline:after': {
                        display: 'none',
                    },
                    // underline color for input with variant='standard'
                    '& .MuiInput-underline:before': {
                        display: 'none',
                    },
                },
                error: {
                    // input label
                    '& .MuiInput-root': {
                        backgroundColor: 'rgba(255,0,0,0.33)',
                        borderRadius: '5px',
                    },
                    // focused underline color for input with variant='standard'
                    '& .MuiInput-underline:after': {
                        display: 'none',
                    },
                    // underline color for input with variant='standard'
                    '& .MuiInput-underline:before': {
                        display: 'none',
                    },
                },
                badge: {
                    color: '#ffffff',
                    backgroundColor: ezprOrange,
                    padding: '0px',
                },
                rootBadge: {
                    margin: 'auto 0px',
                },
            };
        },
        {_theme},
    );
    const classes = useStyles();

    const addThreeDotsMenu = (items) => {
    // Hide delete and ungroup options if there is only one material.
        if (materials.length <= 1) {
            const deleteIndex = items.findIndex((item) => item.title === 'Delete');
            if (deleteIndex !== -1) {
                items.splice(deleteIndex, 1);
            }
            const ungroupIndex = items.findIndex((item) => item.title === 'Ungroup');
            if (ungroupIndex !== -1) {
                items.splice(ungroupIndex, 1);
            }
        }
        return (
            <>
                {materialHasTransportConditions &&
          material.transportCostConditions.length > 0 && (
                    <Badge
                        badgeContent={
                            <Tooltip title="Transport cost condition types">
                                <PriorityHighIcon sx={PRIORITY_HIGH_ICON_SX} />
                            </Tooltip>
                        }
                        classes={{root: classes.rootBadge, badge: classes.badge}}
                    >
                        <ThreeDotsMenu
                            items={items}
                            loading={false}
                            style={{margin: 'auto 0'}}
                            prTag={props.prTag}
                        />
                    </Badge>
                )}
                {(!materialHasTransportConditions ||
          (materialHasTransportConditions &&
            !material.transportCostConditions.length)) && (
                    <ThreeDotsMenu
                        items={items}
                        loading={false}
                        style={{margin: 'auto 0'}}
                        prTag={props.prTag}
                    />
                )}
            </>
        );
    };

    const snackbarAction = (
        <React.Fragment>
            <Button
                color="secondary"
                size="small"
                onClick={() => handleUpdateAction()}
            >
        YES
            </Button>
            <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleCloseSnackbar}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </React.Fragment>
    );

    // For USDN currency, price, quantity and subtotal supports 5 decimals, all other currencies support 3 decimals for qty & 2 for price.
    // noinspection RegExpRedundantEscape
    const decimalRegexForPrice =
    material.currency?.value === 'USDN' ?
        /([0-9]*[.|,]{0,1}[0-9]{0,5})/s :
        /([0-9]*[.|,]{0,1}[0-9]{0,2})/s;
    const decimalRegexForQuantity =
    material.currency?.value === 'USDN' ?
        /([0-9]*[.|,]{0,1}[0-9]{0,5})/s :
        /([0-9]*[.|,]{0,1}[0-9]{0,3})/s;
    const subtotalDecimals = material.currency?.value === 'USDN' ? 5 : 2;

    const addTooltipSourcingEventIDWithBuyer = (sourcingEventID, buyer) => {
        return (
            <Tooltip
                title={
                    <div>
                        {' '}
            Sourcing ID: {sourcingEventID ? sourcingEventID : ''} <br />{' '}
            Responsible Buyer: {source !== 'popup' ? buyer : 'Not Applicable'}{' '}
                    </div>
                }
                placement={'top'}
                arrow
            >
                <TextField
                    className={'pr-editor-item-details-textfield-small'}
                    defaultValue={sourcingEventID}
                    variant={'standard'}
                    size={'small'}
                    disabled
                />
            </Tooltip>
        );
    };

    const isMaterialTaxAmountDependencyMissing = (materialId) => {
        return (
            !companyCode.value ||
            !purchaseOrganization.value ||
            !vendorCode ||
            !defaultPlant.value ||
            (props.materialTaxDependencies[materialId] || '').includes('invalid')
        );
    };

    return (
        <div className={'pr-editor-item-details-container-and-trash'}>
            <div
                className={'pr-editor-item-details-container'}
                style={{height: expanded ? expandedHeight : '60px'}}
            >
                <div className={'pr-editor-item-details-content'}>
                    {materials.length > 1 && (
                        <div
                            className={'pr-editor-item-details-element'}
                            style={{flexGrow: 1}}
                        >
                            <TwoToneCheckbox
                                checked={isSelected}
                                onChange={handleCheckboxChange}
                                disabled={isDisabled('lineItem.select')}
                            />
                        </div>
                    )}
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '0 0 20px', textAlign: 'center'}}
                    >
                        {props.displayedIndex !== undefined ?
                            props.displayedIndex + 1 :
                            material.id + 1}
                    </div>
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '4 1 19%'}}
                    >
                        <div className={classes.root}>
                            {!isPunchOut ?
                                <TextField
                                    disabled
                                    fullWidth
                                    value={formatMaterialName(material.name)}
                                    variant="outlined"
                                    size={'small'}
                                /> :
                                <div
                                    className={'pr-editor-item-details-element'}
                                    style={{flex: '2 2 8%'}}
                                >
                                    <ItemCodeDescriptionDropdown
                                        context={props.context}
                                        plant={material.plant}
                                        getItemCodeDesc={getMaterial}
                                        itemCodeSearchProp={'both'}
                                        l3Category={isNestBuy ? material.l3Category?.split(' - ')[0] : undefined}
                                        onChange={handleItemCodeChange}
                                        selectedValue={checkLabelValueObject(formatMaterialName(material.name))}
                                        getMasterData={getMasterData}
                                        userId={userIdToCallSAPAPIs}
                                        loading={materialNameLoading}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '4 1 17%'}}
                    >
                        <div className={classes.root}>
                            <Tooltip
                                title={`There was an issue with fetching material type. Please try again later.`}
                                placement={'top'}
                                open={showErrorTooltip === 'materialType'}
                                arrow
                                enterNextDelay={500}
                                leaveDelay={3000}
                                onClose={() => setShowErrorTooltip('')}
                            >
                                <div>
                                    <LimitedLengthTextfield
                                        disabled={!editableDescription || isDisabled('lineItem.description')}
                                        value={material.description}
                                        onChange={handleDescriptionChange}
                                        maxLength={40}
                                        variant={'outlined'}
                                        highlightMandatoryFieldForRequester={
                                            highlightMandatoryFieldForRequester
                                        }
                                        highlightMandatoryFieldForSpender={
                                            highlightMandatoryFieldForSpender
                                        }
                                        mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                            'Material description',
                                        )}
                                        mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                            'Material description',
                                        )}
                                    />
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '1 4 6%'}}
                    >
                        <div className={classes.root}>
                            <FormatCurrencySeparatorTextfield
                                disabled={isDisabled('lineItem.quantity')}
                                value={material.quantity.toString()}
                                onChange={handleQuantityChange}
                                decimalRegex={decimalRegexForQuantity}
                                separator={separator}
                                subtotalDecimals={subtotalDecimals}
                                highlightMandatoryFieldForRequester={
                                    highlightMandatoryFieldForRequester
                                }
                                highlightMandatoryFieldForSpender={
                                    highlightMandatoryFieldForSpender
                                }
                                mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                    'Quantity',
                                )}
                                mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                    'Quantity',
                                )}
                            />
                        </div>
                    </div>
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '2 2 8%'}}
                    >
                        <Tooltip
                            title={`There was an issue with fetching ${
                                showErrorTooltip === 'unit' ? 'units' : 'base unit'
                            }. Please try again later.`}
                            placement={'top'}
                            open={
                                showErrorTooltip === 'unit' || showErrorTooltip === 'baseUnit'
                            }
                            arrow
                            enterNextDelay={500}
                            leaveDelay={3000}
                            onClose={() => setShowErrorTooltip('')}
                        >
                            <>
                                <Tooltip title={material.baseUnit && (material.unit !== material.baseUnit) ? `Only base unit (${material.baseUnit}) will be accepted by SAP.` : ''} placement={'top'}>
                                    <div>
                                        {showUnitPopup ? (
                                            <UnitOfMeasureFieldWithPopup
                                                context={props.context}
                                                prTag={props.prTag}
                                                onSave={handleUnitPriceChange}
                                                disabled={unitPopupDisabled}
                                                value={material.unit || material.baseUnit}
                                                leftValue={oldUnitValue.current}
                                                materialNumber={material?.name
                                                    ?.split('-')?.[0]
                                                    ?.trim()
                                                    .padStart(18, '0')}
                                            />
                                        ) : (
                                            <MasterDataDropdown
                                                context={props.context}
                                                property={'Unit of Measure'}
                                                getMasterData={getMasterData}
                                                materialNumber={material?.name
                                                    ?.split('-')?.[0]
                                                    ?.trim()
                                                    .padStart(18, '0')}
                                                disabled={isDisabled('lineItem.unit')}
                                                selectedValue={{
                                                    label: material.unit,
                                                    value: material.unit || material.baseUnit,
                                                }}
                                                userId={userIdToCallSAPAPIs}
                                                onChange={handleUnitPriceChange}
                                                renderOption={(props, option) => (
                                                    option.label === material.baseUnit ?
                                                        <li {...props}>
                                                            <div style={{color: ezprOrange}}>{option.label}</div>
                                                            <span className='custom-dropdown-item-count'>base unit</span>
                                                        </li> :
                                                        <li {...props} style={{display: 'flex'}}>
                                                            {option.label}
                                                            <div className='custom-dropdown-item-count' style={{fontSize: '12px'}}>↔ {parseInt(option.value)} {material.baseUnit}</div>
                                                        </li>
                                                )}
                                                menuWidth={'200px'}
                                                highlightMandatoryFieldForRequester={
                                                    highlightMandatoryFieldForRequester
                                                }
                                                highlightMandatoryFieldForSpender={
                                                    highlightMandatoryFieldForSpender
                                                }
                                                mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                                    'Unit',
                                                )}
                                                mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                                    'Unit',
                                                )}
                                            />
                                        )}
                                    </div>
                                </Tooltip>
                            </>
                        </Tooltip>
                    </div>
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '1 4 6%'}}
                    >
                        <div
                            className={
                                /* !material.price || material.price === '0' ? classes.error : */ classes.root
                            }
                        >
                            <FormatCurrencySeparatorTextfield
                                disabled={isDisabled('lineItem.price')}
                                value={material.price?.toString()}
                                onChange={handlePriceChange}
                                decimalRegex={decimalRegexForPrice}
                                separator={separator}
                                subtotalDecimals={subtotalDecimals}
                                highlightMandatoryFieldForRequester={
                                    highlightMandatoryFieldForRequester
                                }
                                highlightMandatoryFieldForSpender={
                                    highlightMandatoryFieldForSpender
                                }
                                mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                    'Price',
                                )}
                                mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                    'Price',
                                )}
                            />
                        </div>
                    </div>
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '2 2 8%'}}
                    >
                        <MasterDataDropdown
                            property={'Currency'}
                            selectedValue={material.currency}
                            context={props.context}
                            getMasterData={getMasterData}
                            onChange={handleCurrencyChange}
                            filterQueryWithSearch
                            showValue
                            userId={userIdToCallSAPAPIs}
                            recentlyUsed
                            disabled={isDisabled('lineItem.currency')}
                            highlightMandatoryFieldForRequester={
                                highlightMandatoryFieldForRequester
                            }
                            highlightMandatoryFieldForSpender={
                                highlightMandatoryFieldForSpender
                            }
                            mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                'Currency',
                            )}
                            mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                'Currency',
                            )}
                        />
                    </div>
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '1 4 6%'}}
                    >
                        <div className={classes.root}>
                            <NumberTextfieldSmall
                                value={material.per}
                                onChange={handlePerChange}
                                minValue={1}
                                maxLength={5}
                                highlightMandatoryFieldForRequester={
                                    highlightMandatoryFieldForRequester
                                }
                                highlightMandatoryFieldForSpender={
                                    highlightMandatoryFieldForSpender
                                }
                                mandatoryForSpender={mandatoryFieldsForSpender.includes('Per')}
                                mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                    'Per',
                                )}
                                disabled={isDisabled('lineItem.per')}
                            />
                        </div>
                    </div>
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '0 0 140px'}}
                    >
                        <div className={classes.root}>
                            <Tooltip
                                title={material.taxCode ? material.taxCode.label : ''}
                                placement={'top'}
                                arrow
                            >
                                <div>
                                    {' '}
                                    {/* div needed to have tooltip properly functioning */}
                                    <TaxCodeAutomation
                                        context={props.context}
                                        prTag={props.prTag}
                                        material={material}
                                        onChange={handleTaxCodeChange}
                                        selectedValue={material.taxCode}
                                        disabled={isDisabled('lineItem.taxCode')}
                                        highlightMandatoryFieldForRequester={
                                            highlightMandatoryFieldForRequester
                                        }
                                        highlightMandatoryFieldForSpender={
                                            highlightMandatoryFieldForSpender
                                        }
                                        mandatoryForSpender={
                                            Config.getParameter('ENV') !== 'codev' &&
                      mandatoryFieldsForSpender.includes('Tax code')
                                        }
                                        mandatoryForRequester={
                                            Config.getParameter('ENV') !== 'codev' &&
                      mandatoryFieldsForRequester.includes('Tax code')
                                        }
                                        loading={props.loadingCheckDependency}
                                    />
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                    <div
                        className={'pr-editor-item-details-element'}
                        style={{flex: '0 0 110px'}}
                    >
                        {`${formatCurrencySeparator(
                            Material.computeTotalPrice(material).toFixed(subtotalDecimals),
                            separator,
                            subtotalDecimals,
                        )} ${material.currency.value}`}
                    </div>
                    <div
                        onClick={handleClick}
                        className={'pr-editor-item-details-element'}
                        style={{cursor: 'pointer', flex: '0 0 25px'}}
                    >
                        {expanded ? (
                            <i className="material-icons">{'expand_more'}</i>
                        ) : (
                            <i className="material-icons">{'chevron_right'}</i>
                        )}
                    </div>
                </div>
                <div
                    className={'pr-editor-item-details-horizontal-separator'}
                    style={{display: expanded ? 'flex' : 'none'}}
                />
                <div
                    className={'pr-editor-item-details-expandable-infos'}
                    style={{display: expanded ? 'flex' : 'none'}}
                >
                    <div
                        className={'pr-editor-item-details-grid'}
                        style={{
                            gridTemplateRows: `repeat(${showTaxAmount ? '7' : '6'}, 50px)`,
                        }}
                    >
                        <p>L3 Category:</p>
                        <TextField
                            disabled
                            value={material.l3Category}
                            variant={'standard'}
                            size={'small'}
                        />
                        <p>Vendor material no:</p>
                        <TextField
                            defaultValue={material.supplierPartId}
                            variant={'standard'}
                            size={'small'}
                            onBlur={handleVendorMaterialNumberChange}
                            disabled={isDisabled('lineItem.vendorMaterialNumber')}
                        />
                        <p>Requested delivery date: *</p>
                        <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            adapterLocale={enUS}
                        >
                            <DatePicker
                                minDate={isDisabled('lineItem.requestedDeliveryDate') ? null : minDate}
                                value={material.requestedDeliveryDate}
                                inputFormat={dateFormat || 'dd.MM.yyyy'}
                                onChange={handleRequestedDeliveryDateChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant={'standard'}
                                        size={'small'}
                                        sx={{
                                            backgroundColor:
                        mandatoryFieldsForSpender.includes(
                            'Requested delivery date',
                        ) && highlightMandatoryFieldForSpender ?
                            bgColorSpender :
                            mandatoryFieldsForRequester.includes(
                                'Requested delivery date',
                            ) && highlightMandatoryFieldForRequester ?
                                bgColorRequester :
                                null,
                                        }}
                                    />
                                )}
                                disabled={isDisabled('lineItem.requestedDeliveryDate')}
                            />
                        </LocalizationProvider>
                        <div className={'pr-editor-label-with-question-mark'}>
                            <p>Free of charge:</p>
                        </div>
                        <div style={{flex: '0 0 40px'}}>
                            <Checkbox
                                checked={material.freeItem}
                                onChange={handleFreeItemChange}
                                disabled={isDisabled('lineItem.freeOfCharge')}
                            />
                        </div>
                        {isPunchOut ? (
                            <>
                                <p style={{marginRight: '0.5rem'}}>Supplier part auxiliary ID:</p>
                                <TextField
                                    defaultValue={material.supplierPartAuxiliaryId}
                                    variant={'standard'}
                                    size={'small'}
                                    disabled
                                />
                            </>
                        ) :
                            (
                                <>
                                    <div className={'pr-editor-label-with-question-mark'}>
                                        <p className={'pr-editor-item-details-label-small'} style={{marginRight: '0.5rem'}}>PSL ID:</p>
                                        <TextField
                                            className={'pr-editor-item-details-textfield-small'}
                                            defaultValue={material.pslId}
                                            variant={'standard'}
                                            size={'small'}
                                            disabled
                                        />
                                    </div>
                                    <div
                                        className={'pr-editor-label-with-question-mark'}
                                        style={{marginLeft: '1rem'}}
                                    >
                                        <p className={'pr-editor-item-details-label-small'} style={{marginRight: '0.5rem'}}>PSL Material ID:</p>
                                        <TextField
                                            className={'pr-editor-item-details-textfield-small'}
                                            defaultValue={material.uniqueSourcingToolId}
                                            variant={'standard'}
                                            size={'small'}
                                            disabled
                                        />
                                    </div>
                                </>
                            )}
                        <div className={'pr-editor-label-with-question-mark'}>
                            <p style={{marginRight: '0.5rem', whiteSpace: 'nowrap'}}>
                Sourcing Event ID:
                            </p>
                            {addTooltipSourcingEventIDWithBuyer(sourcingEventID, buyer)}
                        </div>
                        <div
                            className={'pr-editor-label-with-question-mark'}
                            style={{marginLeft: '1rem'}}
                        >
                            <p className={'pr-editor-item-details-label-small'} style={{marginRight: '0.5rem'}}>Contract ID:</p>
                            <TextField
                                className={'pr-editor-item-details-textfield-small'}
                                disabled
                                defaultValue={contractID}
                                variant={'standard'}
                                size={'small'}
                            />
                        </div>
                        {showTaxAmount && <div />}
                        <div className="pr-editor-grid-fullscreen-textfield">
                            <TextField
                                fullWidth
                                id="outlined-multiline-external-comments"
                                label="Comments for supplier"
                                multiline
                                rows={4}
                                focused
                                defaultValue={material.supplierComments}
                                onBlur={(event) =>
                                    handleSupplierCommentsChange(event.target.value)
                                }
                                disabled={isDisabled('lineItem.supplierComments')}
                                style={{marginTop: '12px'}}
                            />
                        </div>
                    </div>
                    <div className={'pr-editor-item-details-vertical-separator'} />
                    <div
                        className={'pr-editor-item-details-grid'}
                        style={{
                            gridTemplateRows: `repeat(${showTaxAmount ? '7' : '6'}, 50px)`,
                        }}
                    >
                        <p>Plant: *</p>
                        {expanded && (
                            <MasterDataDropdown
                                error={
                                    !(Array.isArray(material.plant) ?
                                        material.plant[0]?.label :
                                        material.plant.label)
                                }
                                property={'Plant'}
                                selectedValue={
                                    Array.isArray(material.plant) ?
                                        material.plant[0] :
                                        material.plant
                                }
                                context={props.context}
                                getMasterData={getMasterData}
                                onChange={handlePlantChange}
                                selectedCompanyCode={companyCode}
                                filterQueryWithSearch
                                userId={userIdToCallSAPAPIs}
                                recentlyUsed
                                disabled={isDisabled('lineItem.plant')}
                                highlightMandatoryFieldForRequester={
                                    highlightMandatoryFieldForRequester
                                }
                                highlightMandatoryFieldForSpender={
                                    highlightMandatoryFieldForSpender
                                }
                                mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                    'Plant',
                                )}
                                mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                    'Plant',
                                )}
                                loading={props.loadingCheckDependency}
                            />
                        )}
                        <p>Storage location:</p>
                        {expanded && (
                            <MasterDataDropdown
                                property={'Storage Location'}
                                selectedValue={material.storageLocation}
                                context={props.context}
                                getMasterData={getMasterData}
                                onChange={handleChangeStorageLocation}
                                selectedPlant={
                                    Array.isArray(material.plant) ?
                                        material.plant[0] :
                                        material.plant
                                }
                                filterQueryWithSearch={true}
                                userId={userIdToCallSAPAPIs}
                                recentlyUsed
                                disabled={isDisabled('lineItem.storageLocation')}
                                loading={props.loadingCheckDependency}
                            />
                        )}

                        <p>Delivery address: *</p>
                        <DeliveryAddressField
                            loading={deliveryAddressLoading}
                            value={material.deliveryAddress}
                            onSave={handleDeliveryAddressChange}
                            disableEdit={isDisabled('lineItem.deliveryAddress')}
                            highlightMandatoryFieldForRequester={
                                highlightMandatoryFieldForRequester
                            }
                            highlightMandatoryFieldForSpender={
                                highlightMandatoryFieldForSpender
                            }
                            mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                'Delivery address',
                            )}
                            mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                'Delivery address',
                            )}
                        />
                        <p>PPM Cost Plan</p>
                        <LimitedLengthTextfield
                            value={material.ppmCostPlan}
                            onChange={handlePPMCostPlanChange}
                            variant={'standard'}
                            size={'small'}
                            maxLength={12}
                            disabled={isDisabled('lineItem.ppmCostPlan')}
                        />
                        <p>Source</p>
                        <TextField
                            value={material.source?.label}
                            disabled
                            variant={'standard'}
                            size={'small'}
                        />
                        <>
                            <div className={'pr-editor-label-with-question-mark'} style={{marginRight: '1rem'}}>
                                <p className={'pr-editor-item-details-label-small'}>Material type:</p>
                                <TextField
                                    className={'pr-editor-item-details-textfield-small'}
                                    disabled
                                    defaultValue={material.type}
                                    variant={'standard'}
                                    size={'small'}
                                />
                            </div>
                            <div className={'pr-editor-label-with-question-mark'}>
                                <p className={'pr-editor-item-details-label-small'}>Supplier status:</p>
                                <TextField
                                    className={'pr-editor-item-details-textfield-small'}
                                    size={'small'}
                                    disabled
                                    defaultValue={material.supplierStatus}
                                    variant={'standard'}
                                />
                            </div>
                        </>
                        {showTaxAmount && (
                            <>
                                <div
                                    className="pr-editor-label-with-question-mark"
                                    style={{display: 'flex', alignItems: 'center'}}
                                >
                                    <p style={{marginRight: '0.5rem'}}>Tax amount:</p>
                                    <Tooltip
                                        arrow
                                        placement="top"
                                        title={
                                            <>
                                                <strong>
                                                    Complete further your request data first so we can check and return the accurate tax amount
                                                </strong>
                                                <br /><br />
                                                For instance: Company code, Purchasing Organisation, Supplier code, Plant, Delivery address, Material code, Cost Object i.e. Cost Center, WBS, others – are criteria the system needs to determine the tax amount
                                            </>
                                        }
                                    >
                                        <span>
                                            <HelpOutlineIcon
                                                fontSize="small"
                                                style={{cursor: 'pointer'}}
                                            />
                                        </span>
                                    </Tooltip>
                                </div>
                                <Tooltip
                                    title={taxCodeFromMaterial ? `Tax code from material: ${taxCodeFromMaterial}` : null}
                                    placement="top"
                                >
                                    <TextField
                                        disabled
                                        value={
                                            isMaterialTaxAmountDependencyMissing(material.id) ?
                                                'TBC' :
                                                `${material.taxAmount || '0'} ${material.currency?.value}`
                                        }
                                        variant="standard"
                                        size="small"
                                        InputProps={{
                                            startAdornment: props.isTaxAmountLoading && (
                                                <InputAdornment position="start">
                                                    <CircularProgress size={18} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Tooltip>
                            </>
                        )}
                        <div className="pr-editor-grid-fullscreen-textfield">
                            <TextField
                                fullWidth={true}
                                id="outlined-multiline-external-comments"
                                label="Comments (internal only)"
                                multiline
                                rows={4}
                                focused={true}
                                defaultValue={material.internalComments}
                                onBlur={(event) =>
                                    handleInternalCommentsChange(event.target.value)
                                }
                                disabled={isDisabled('lineItem.internalComments')}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {!isDisabled('lineItem.rowActions') &&
        addThreeDotsMenu([
            {
                title: 'Copy',
                onClick: handleClickOnCopy,
                closeOnClick: true,
                disabled: isDisabled('lineItem.copy'),
            },
            {
                title: 'Delete',
                onClick: handleClickOnDelete,
                closeOnClick: true,
            },
            {
                title: 'Ungroup',
                onClick: handleClickOnUngroup,
                closeOnClick: true,
            },
            ...(materialHasTransportConditions ?
                [
                    {
                        title: 'Edit Condition Types',
                        onClick: handleClickOnEditTransportCostConditions,
                        closeOnClick: true,
                    },
                ] :
                []),
        ])}
            {isDisabled('lineItem.rowActions') &&
        materialHasTransportConditions &&
        addThreeDotsMenu([
            {
                title: 'View Condition Types',
                onClick: handleClickOnEditTransportCostConditions,
                closeOnClick: true,
            },
        ])}
            <CustomPopup
                buttons={[
                    <Button key={1} onClick={() => setRemovePopupOpen(false)} autoFocus>
            Cancel
                    </Button>,
                    <Button key={2} onClick={handleClickOnDeleteMaterial}>
            Delete
                    </Button>,
                ]}
                open={removePopupOpen}
                handleClose={() => setRemovePopupOpen(false)}
            />
            {materials.length > 1 && props.selectedMaterialIds.length > 0 ? (
                <CustomPopup
                    title="Ungroup item"
                    content="Are you sure you want to ungroup all selected items?"
                    buttons={[
                        <Button
                            key={1}
                            onClick={() => setUngroupPopupOpen(false)}
                            autoFocus
                        >
              Cancel
                        </Button>,
                        <Button key={2} onClick={() => handleClickOnUngroupMaterial(true)}>
              Ungroup
                        </Button>,
                    ]}
                    open={ungroupPopupOpen}
                    handleClose={() => setUngroupPopupOpen(false)}
                />
            ) : (
                <CustomPopup
                    title="Ungroup item"
                    content="Are you sure you want to ungroup this item?"
                    buttons={[
                        <Button
                            key={1}
                            onClick={() => setUngroupPopupOpen(false)}
                            autoFocus
                        >
              Cancel
                        </Button>,
                        <Button key={2} onClick={() => handleClickOnUngroupMaterial(false)}>
              Ungroup
                        </Button>,
                    ]}
                    open={ungroupPopupOpen}
                    handleClose={() => setUngroupPopupOpen(false)}
                />
            )}
            <CustomPopup
                title="Error"
                content={CurrencyController.errorMessage(conversionFailed)}
                buttons={[
                    <Button
                        key={0}
                        onClick={() => setShowCurrencyErrorPopup(false)}
                        autoFocus
                    >
            Ok
                    </Button>,
                ]}
                open={showCurrencyErrorPopup}
                handleClose={() => setShowCurrencyErrorPopup(false)}
                showErrorIcon={true}
            />
            <CustomPopup
                title="Error"
                content={errorReason}
                buttons={[
                    <Button
                        key={0}
                        onClick={() => {
                            setErrorReason('');
                            setShowAPIErrorPopup(false);
                        }}
                        autoFocus
                    >
            Ok
                    </Button>,
                ]}
                open={showAPIErrorPopup}
                handleClose={() => {
                    setErrorReason('');
                    setShowAPIErrorPopup(false);
                }}
                showErrorIcon={true}
            />
            <Snackbar
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={'Do you want to modify all selected line items?'}
                action={snackbarAction}
            />
        </div>
    );
}

LineItem.propTypes = {
    materialId: PropTypes.number,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    prTag: PropTypes.string,
    selectedMaterialIds: PropTypes.array,
    onSelectMaterial: PropTypes.func,
    setSelectedMaterialIds: PropTypes.func,
    updateSelectedMaterials: PropTypes.func,
    setShowViaConditionPopup: PropTypes.func,
    setSelectedTransportCostMaterialId: PropTypes.func,
    setIsItemUngrouped: PropTypes.func,
    loadingCheckDependency: PropTypes.bool,
    displayedIndex: PropTypes.number,
    isTaxAmountLoading: PropTypes.bool,
    isTaxAmountDependencyMissing: PropTypes.bool,
    materialTaxDependencies: PropTypes.object,
};
